import React, { useState, useEffect } from 'react';
import { Grid, Card, Button, Skeleton } from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { fetchTableData } from '../../../../api/tableView';

const formatCustomDate = (dateString) => {
  try {
    if (!dateString) {
      return 'N/A'; // Return N/A for null/undefined/empty values
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'N/A'; // Return N/A for invalid dates
    }
    return format(date, 'dd-MM-yyyy');
  } catch (error) {
    console.warn('Date parsing error:', error);
    return 'N/A';
  }
};

const NearExpiryItem = ({ ean, expiryDate, batchNo, loading }) => (
  <div className="bg-white rounded p-2 mb-2 text-sm shadow-sm">
    {loading ? (
      <>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="40%" height={20} />
        <Skeleton variant="text" width="30%" height={20} />
      </>
    ) : (
      <div className="flex items-start gap-2">
        <FiberManualRecordIcon 
          sx={{ 
            color: '#ffa000',
            fontSize: '12px',
            marginTop: '4px'
          }} 
        />
        <div>
          <div className="font-bold">EAN: {ean}</div>
          <div className="text-xs text-gray-500">
            Expires on: {formatCustomDate(expiryDate)}
          </div>
          <div className="text-xs text-gray-500">
            Batch: {batchNo || 'N/A'}
          </div>
        </div>
      </div>
    )}
  </div>
);

const NearingExpiryCard = () => {
  const { storeId } = useParams(); 
  const navigate = useNavigate();
  const [nearExpiryStats, setNearExpiryStats] = useState({
    nearExpiryCount: 0,
    nearExpiryProducts: []
  });
  const [loading, setLoading] = useState(true);
  const selectedDate = useSelector((state) => state.customization.selectedDate);

  useEffect(() => {
    const mainAbortController = new AbortController();
    const tableAbortController = new AbortController();
    
    const fetchStats = async () => {
      try {
        setLoading(true);
        const currentDate = selectedDate || new Date();
        const formattedDate = format(currentDate, 'yyyy-MM-dd');

        // Fetch near expiry products list (count comes from pagination metadata)
        const tableResponse = await fetchTableData({
          source_id: storeId,
          start_date: formattedDate,
          end_date: formattedDate,
          page: 1,
          expired: false,
          almost_expired: true
        }, tableAbortController.signal);

        if (!mainAbortController.signal.aborted && !tableAbortController.signal.aborted) {
          setNearExpiryStats({
            nearExpiryCount: tableResponse.total_count || 0,
            nearExpiryProducts: tableResponse.data
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching near expiry data:', error);
          setNearExpiryStats({
            nearExpiryCount: 0,
            nearExpiryProducts: []
          });
        }
      } finally {
        if (!mainAbortController.signal.aborted && !tableAbortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      mainAbortController.abort();
      tableAbortController.abort();
    };
    // eslint-disable-next-line
  }, [selectedDate]);

  return (
    <Grid item xs={12} sm={12} md={2.4} lg={2.4} xl={2.4}>
      <Card 
        className="border relative border-gray-300" 
        sx={{ 
          height: "276px",
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px'
        }}
      >
        <div className="flex w-full flex-col gap-1 p-3">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <TimerIcon className="bg-[#ffa000] text-white rounded-full p-2 text-6xl" />
              <div style={{ width: loading ? '120px' : 'auto' }}>
                {loading ? (
                  <>
                    <Skeleton variant="text" width="100%" height={40} />
                    <Skeleton variant="text" width="80%" height={28} />
                  </>
                ) : (
                  <>
                    <p className="text-3xl">{nearExpiryStats.nearExpiryCount}</p>
                    <p className="text-lg font-semibold">Nearing Expiry</p>
                  </>
                )}
              </div>
            </div>
            {!loading && (
              <Button
                className="absolute text-xs right-2 top-2"
                endIcon={<ArrowForwardIcon sx={{ fontSize: "small" }} />}
                onClick={() => navigate(`/main/stores/storeinsight/tableview/${storeId}/NEAR EXPIRY`)}
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#ffa000",
                    textDecoration: "underline"
                  },
                }}
              >
                View all
              </Button>
            )}
          </div>
          <div className="bg-slate-100 flex-grow overflow-y-auto h-[184px] p-2 rounded-lg scrollbar">
            <div className="text-xs text-gray-500 mb-2 italic">
              Products nearing expiry
            </div>
            {loading ? (
              [...Array(3)].map((_, index) => (
                <NearExpiryItem key={index} loading={true} />
              ))
            ) : nearExpiryStats.nearExpiryProducts.length > 0 ? (
              nearExpiryStats.nearExpiryProducts.map((product, index) => (
                <NearExpiryItem
                  key={index}
                  ean={product.ean}
                  expiryDate={product.expiry_date}
                  batchNo={product.batch_no}
                  loading={false}
                />
              ))
            ) : (
              <div className="text-sm text-gray-500 text-center py-4">
                No products nearing expiry
              </div>
            )}
          </div>
        </div>
      </Card>
    </Grid>
  );
};

export default NearingExpiryCard;
